/**
 * Top-level React error boundary.
 *
 * Wrapped around the entire app in `src/frontend/App.tsx`. Catches any
 * exception thrown during render or in lifecycle methods of a
 * descendant component (NOT event handlers — those go through
 * `reportClientError` directly).
 *
 * On error: reports to `/v1/client-errors` (best-effort) and shows a
 * minimal recovery UI with a "Reload" button. Without this, an
 * uncaught render error would blank the whole SPA with no telemetry
 * back to the server.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "../lib/error-reporter.ts";

interface Props {
  children: ReactNode;
  /** Optional scope override for nested boundaries; defaults to "root". */
  scope?: string;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, errorMessage: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportClientError({
      scope: this.props.scope ?? "root",
      error,
      componentStack: info.componentStack ?? undefined,
    });
  }

  handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary-fallback" role="alert">
        <h1>Something went wrong</h1>
        <p>
          The app hit an unexpected error and couldn't render this view. The error has been logged
          for the team to look at.
        </p>
        {this.state.errorMessage && (
          <pre className="error-boundary-message">{this.state.errorMessage}</pre>
        )}
        <button type="button" onClick={this.handleReload}>
          Reload the page
        </button>
      </div>
    );
  }
}
